import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Minus, Plus, RotateCcw, Scale } from 'lucide-react';
import { Fragment, useState } from 'react';

import {
  MAX_COEFFICIENT,
  MIN_COEFFICIENT,
  WORD_EQUATION_CASES,
  checkFormulas,
  checkRoles,
  checkWordBalance,
  substancesForSorting,
  type BalanceCheck,
  type FormulaCheck,
  type RoleCheck,
  type SubstanceRole,
  type WordEquationCase,
  type WordSubstance,
} from './conservationOfMass';
import { AtomTally, FeedbackPanel, Formula } from './ConservationOfMassVisuals';

type Stage = 'roles' | 'formulas' | 'balance';

const ROLE_LABELS: Record<SubstanceRole, string> = { reactant: 'Reactant', product: 'Product' };
const ROLES: SubstanceRole[] = ['reactant', 'product'];

const BALANCE_TITLES: Record<BalanceCheck['status'], string> = {
  balanced: 'Balanced — mass is conserved',
  'not-simplest': 'Balanced, but not in the simplest ratio',
  unbalanced: 'Not balanced yet',
  invalid: 'Check the coefficients',
};

function initialFormulas(entry: WordEquationCase): Record<string, string | undefined> {
  return Object.fromEntries(
    entry.substances.map((substance) => [
      substance.name,
      substance.formulaOptions.length === 1 ? substance.formula : undefined,
    ]),
  );
}

function StepHeading({ step, title, active }: { step: number; title: string; active: boolean }) {
  return (
    <h4 className={`text-sm font-extrabold uppercase tracking-[0.12em] ${active ? 'text-brand-coral' : 'text-slate2'}`}>
      Step {step} · {title}
    </h4>
  );
}

function CoefficientStepper({
  substance,
  value,
  onChange,
}: {
  substance: WordSubstance;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl border border-hairline bg-surface px-2 py-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Decrease coefficient for ${substance.name}`}
          disabled={value <= MIN_COEFFICIENT}
          onClick={() => onChange(value - 1)}
          className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-40"
        >
          <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <span className="min-w-[1.5rem] text-center text-lg font-extrabold text-brand-coral">{value}</span>
        <button
          type="button"
          aria-label={`Increase coefficient for ${substance.name}`}
          disabled={value >= MAX_COEFFICIENT}
          onClick={() => onChange(value + 1)}
          className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      <span className="text-base font-extrabold text-ink">
        <Formula formula={substance.formula} />
      </span>
    </div>
  );
}

export function WordEquationMode() {
  const [entry, setEntry] = useState<WordEquationCase>(WORD_EQUATION_CASES[0]);
  const [stage, setStage] = useState<Stage>('roles');
  const [roles, setRoles] = useState<Record<string, SubstanceRole | undefined>>({});
  const [formulas, setFormulas] = useState(() => initialFormulas(WORD_EQUATION_CASES[0]));
  const [coefficients, setCoefficients] = useState<number[]>(() => WORD_EQUATION_CASES[0].substances.map(() => MIN_COEFFICIENT));
  useScienceTutorSection('WordEquationMode', { entry: tutorSelection(entry), stage, roles, formulas, coefficients });
  const [roleResult, setRoleResult] = useState<RoleCheck | null>(null);
  const [formulaResult, setFormulaResult] = useState<FormulaCheck | null>(null);
  const [balanceResult, setBalanceResult] = useState<BalanceCheck | null>(null);

  const choose = (next: WordEquationCase) => {
    setEntry(next);
    setStage('roles');
    setRoles({});
    setFormulas(initialFormulas(next));
    setCoefficients(next.substances.map(() => MIN_COEFFICIENT));
    setRoleResult(null);
    setFormulaResult(null);
    setBalanceResult(null);
  };

  const checkRoleStep = () => {
    const result = checkRoles(entry, roles);
    setRoleResult(result);
    if (result.correct) setStage('formulas');
  };
  const checkFormulaStep = () => {
    const result = checkFormulas(entry, formulas);
    setFormulaResult(result);
    if (result.correct) setStage('balance');
  };

  const reactants = entry.substances.map((substance, index) => ({ substance, index })).filter(({ substance }) => substance.role === 'reactant');
  const products = entry.substances.map((substance, index) => ({ substance, index })).filter(({ substance }) => substance.role === 'product');
  const formulaChoices = entry.substances.filter((substance) => substance.formulaOptions.length > 1);
  const allRolesChosen = entry.substances.every((substance) => roles[substance.name]);

  const steppers = (items: { substance: WordSubstance; index: number }[]) =>
    items.map(({ substance, index }, position) => (
      <Fragment key={substance.name}>
        {position > 0 ? <span className="self-center text-lg font-extrabold text-slate2">+</span> : null}
        <CoefficientStepper
          substance={substance}
          value={coefficients[index]}
          onChange={(value) => {
            setCoefficients((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
            setBalanceResult(null);
          }}
        />
      </Fragment>
    ));

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Choose a reaction</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1" aria-label="Word equations">
          {WORD_EQUATION_CASES.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === entry.id}
              onClick={() => choose(candidate)}
              className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                candidate.id === entry.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
              }`}
            >
              {candidate.title}
            </button>
          ))}
        </div>
      </aside>

      <section className="min-w-0 space-y-6 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div>
          <h3 className="text-xl font-extrabold text-ink">{entry.title}</h3>
          <p className="mt-2 text-sm font-extrabold uppercase tracking-[0.1em] text-slate2">Word equation</p>
          <p className="mt-1 text-lg font-bold text-ink">{entry.wordEquation}</p>
        </div>

        <div>
          <StepHeading step={1} title="Reactant or product?" active={stage === 'roles'} />
          <div className="mt-3 grid gap-2">
            {substancesForSorting(entry).map((substance) => (
              <div key={substance.name} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-surface px-4 py-2">
                <span className="text-sm font-extrabold text-ink">{substance.name}</span>
                <div className="flex gap-2" role="group" aria-label={`Role of ${substance.name}`}>
                  {ROLES.map((role) => (
                    <button
                      key={role}
                      type="button"
                      disabled={stage !== 'roles'}
                      aria-pressed={roles[substance.name] === role}
                      onClick={() => setRoles((current) => ({ ...current, [substance.name]: role }))}
                      className={`rounded-full border px-3 py-1.5 text-xs font-extrabold disabled:cursor-default ${
                        roles[substance.name] === role ? 'border-ink bg-ink text-white' : 'border-hairline bg-white text-ink'
                      }`}
                    >
                      {ROLE_LABELS[role]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {stage === 'roles' ? (
            <button
              type="button"
              disabled={!allRolesChosen}
              onClick={checkRoleStep}
              className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Check roles
            </button>
          ) : null}
          {roleResult ? (
            <FeedbackPanel correct={roleResult.correct} title={roleResult.correct ? 'Correct — roles identified' : 'Not quite — read the arrow'}>
              <p>{roleResult.feedback}</p>
            </FeedbackPanel>
          ) : null}
        </div>

        {stage !== 'roles' ? (
          <div>
            <StepHeading step={2} title="Choose each formula" active={stage === 'formulas'} />
            {formulaChoices.length === 0 ? (
              <p className="mt-2 text-sm font-medium text-slate2">Every substance here has only one formula.</p>
            ) : (
              <div className="mt-3 grid gap-3">
                {formulaChoices.map((substance) => (
                  <div key={substance.name} className="rounded-2xl bg-surface px-4 py-3">
                    <p className="text-sm font-extrabold text-ink">{substance.name}</p>
                    <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`Formula for ${substance.name}`}>
                      {substance.formulaOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          disabled={stage !== 'formulas'}
                          aria-pressed={formulas[substance.name] === option}
                          aria-label={`${substance.name} as ${option}`}
                          onClick={() => setFormulas((current) => ({ ...current, [substance.name]: option }))}
                          className={`rounded-xl border px-3 py-1.5 text-base font-extrabold disabled:cursor-default ${
                            formulas[substance.name] === option ? 'border-ink bg-ink text-white' : 'border-hairline bg-white text-ink'
                          }`}
                        >
                          <Formula formula={option} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {stage === 'formulas' ? (
              <button
                type="button"
                onClick={checkFormulaStep}
                className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white"
              >
                Check formulas
              </button>
            ) : null}
            {formulaResult ? (
              <FeedbackPanel correct={formulaResult.correct} title={formulaResult.correct ? 'Correct — formulas chosen' : 'Not quite — check the formulas'}>
                {formulaResult.correct ? (
                  <p>Each name now has its correct formula. Next, balance the atoms.</p>
                ) : (
                  formulaResult.messages.map((message) => <p key={message}>{message}</p>)
                )}
              </FeedbackPanel>
            ) : null}
          </div>
        ) : null}

        {stage === 'balance' ? (
          <div>
            <StepHeading step={3} title="Balance the formula equation" active />
            <div className="mt-3 flex flex-wrap items-stretch gap-2" aria-label="Equation coefficients">
              {steppers(reactants)}
              <span className="self-center px-1 text-xl font-extrabold text-ink">→</span>
              {steppers(products)}
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setBalanceResult(checkWordBalance(entry, coefficients))}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white"
              >
                <Scale className="h-4 w-4" aria-hidden="true" /> Check balance
              </button>
            </div>
            {balanceResult ? (
              <FeedbackPanel correct={balanceResult.status === 'balanced'} title={BALANCE_TITLES[balanceResult.status]}>
                <p>{balanceResult.message}</p>
                {balanceResult.atoms.length > 0 ? <AtomTally atoms={balanceResult.atoms} /> : null}
              </FeedbackPanel>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => choose(entry)}
          className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-2.5 text-sm font-extrabold text-ink hover:bg-surface"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" /> Start this reaction again
        </button>
      </section>
    </div>
  );
}
